export type GamePerformanceStatus =
	| 'RecommendedRequirementsMet'
	| 'MinimumRequirementsMet'
	| 'RequirementsNotMet'
	| 'NotKnown';

export interface CoverImage {
	url: string;
	width: number | null;
	height: number | null;
}

export interface GameThumbnail {
	id: string;
	title: string;
	coverImage: CoverImage | null;
	thumbnailImage: CoverImage | null;
}

export interface GameTitleEntry {
	id: string;
	title: string;
	alternativeTitles: string[];
}

export interface GamePerformanceCalculation {
	title: string;
	coverImage: string | null;
	status: GamePerformanceStatus;
}

export interface DebugComponentMatch {
	productName: string;
	matchedPerformanceName: string | null;
	componentType: string | null;
	performanceValue: number | null;
}

export interface DebugComponentDetail {
	componentType: string;
	yourValue: number | null;
	minimumName: string | null;
	minimumValue: number | null;
	recommendedName: string | null;
	recommendedValue: number | null;
	status: string;
}

export interface DebugGameStatus {
	title: string;
	status: string;
	components: DebugComponentDetail[];
}

export interface DebugGamePerformanceResult {
	componentMatches: DebugComponentMatch[];
	games: DebugGameStatus[];
}

export interface GamePerformanceCalculationResult {
	baselineProductName: string;
	games: GamePerformanceCalculation[];
	allStatuses: GamePerformanceStatus[];
	debugGamePerformanceResult: DebugGamePerformanceResult | null;
}

const API_BASE_URL = '/configure/api/games';

export async function calculateGamePerformance(
	productNames: string[],
	gameTitles: string[]
): Promise<GamePerformanceCalculationResult> {
	const response = await fetch(`${API_BASE_URL}/calculate`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ productNames, gameTitles }),
	});

	if (!response.ok) throw new Error('Game performance calculation failed');

	return response.json();
}

export async function getMostPopularGames(): Promise<GameThumbnail[]> {
	const response = await fetch(`${API_BASE_URL}/topgames`);

	if (!response.ok) throw new Error('Failed to fetch most popular games');

	return response.json();
}

export async function getAllGameTitles(): Promise<GameTitleEntry[]> {
	const response = await fetch(`${API_BASE_URL}/titles`);

	if (!response.ok) throw new Error('Failed to fetch game titles');

	return response.json();
}

export async function getGamesByTitles(titles: string[]): Promise<GameThumbnail[]> {
	const response = await fetch(`${API_BASE_URL}/details`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(titles),
	});

	if (!response.ok) throw new Error('Failed to fetch game details');

	return response.json();
}